import React from "react";
import { Form, Row, Col } from "react-bootstrap";
import { Plus, XCircleFill } from "react-bootstrap-icons";

const AgentSection = ({
  formData,
  formErrors,
  onInputChange,
  agentOptions,
  showAgentDropdown,
  loadingAgents,
  selectedAgentIndex,
  agentInputRef,
  dropdownRef,
  fetchDefaultAgents,
  handleAgentFocus,
  handleAgentKeyDown,
  handleAgentSelect,
  handleAgentChange,
  handleClearAgent,
  setSelectedAgentIndex,
}) => {

  // Initialize on focus
  const handleFocus = () => {
    handleAgentFocus();
    if (!formData.agent) {
      fetchDefaultAgents();
    }
  };

  return (
    <div className="appointment-section">
      <h6 className="section-title" style={{fontWeight: "bold"}}>Agent</h6>
      <div className="section-fields">
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Agent</Form.Label>
              <div className="agent-search-container" ref={dropdownRef} style={{ position: 'relative' }}>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    ref={agentInputRef}
                    type="text"
                    placeholder="Search Agent Name"
                    value={formData.agent}
                    onChange={(e) => handleAgentChange(e.target.value, onInputChange)}
                    onFocus={handleFocus}
                    onKeyDown={(e) => handleAgentKeyDown(e, formData.agent, (agent) => handleAgentSelect(agent, onInputChange))}
                    isInvalid={!!formErrors.agent}
                    autoComplete="off"
                    style={{ paddingRight: formData.agent ? '2.5rem' : '0.75rem' }}
                  />
                  {formData.agent && (
                    <XCircleFill
                      size={16}
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#6C757D',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleClearAgent(onInputChange)}
                    />
                  )}
                </div>
                
                {showAgentDropdown && (
                  <div 
                    className="agent-dropdown"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderTop: 'none',
                      borderRadius: '0 0 0.375rem 0.375rem',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
                    }}
                  >
                    {loadingAgents ? (
                      <div 
                        style={{
                          padding: '1rem 0.75rem',
                          textAlign: 'center'
                        }}
                      >
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading agents...</span>
                        </div>
                      </div>
                    ) : agentOptions.length === 0 && !loadingAgents ? (
                      <>
                        <div 
                          style={{
                            padding: '0.75rem',
                            color: '#ADB5BD',
                            textAlign: 'center',
                            fontSize: '0.875rem'
                          }}
                        >
                          {!formData.agent ? 'No agents yet. Create the agent below.' : 'No matching agents.'}
                        </div>
                        <div style={{ borderTop: '1px solid #f8f9fa' }}></div>
                        <div
                          className="agent-option"
                          style={{
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                          onClick={() => {
                            window.open('/agent-management/new', '_blank');
                          }}
                        >
                          <Plus size={16} style={{ color: '#000' }} />
                          <span style={{ fontWeight: '500', color: '#000' }}>New Agent</span>
                        </div>
                      </>
                    ) : (
                      agentOptions.map((agent, index) => {
                        const displayName = agent.name || agent.full_name || 
                          (agent.first_name && agent.last_name ? `${agent.first_name} ${agent.last_name}` : '') || 
                          agent.email || 'Unknown Agent';
                        
                        return (
                          <div
                            key={agent.id || index}
                            className={`agent-option ${index === selectedAgentIndex ? 'selected' : ''}`}
                            style={{
                              padding: '0.5rem 0.75rem',
                              cursor: 'pointer',
                              backgroundColor: index === selectedAgentIndex ? '#e9ecef' : 'transparent',
                              borderBottom: index < agentOptions.length - 1 ? '1px solid #f8f9fa' : 'none'
                            }}
                            onClick={() => handleAgentSelect(agent, onInputChange)}
                            onMouseEnter={() => setSelectedAgentIndex(index)}
                          >
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center' 
                            }}>
                              <span style={{ fontWeight: '500' }}>{displayName}</span>
                              {agent.agency && (
                                <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>{agent.agency}</span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                    
                    {/* Always show New Agent option when there are agents and not loading */}
                    {agentOptions.length > 0 && !loadingAgents && (
                      <>
                        <div style={{ borderTop: '1px solid #f8f9fa' }}></div>
                        <div
                          className="agent-option"
                          style={{
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                          onClick={() => {
                            window.open('/agent-management/new', '_blank');
                          }}
                        >
                          <Plus size={16} style={{ color: '#000' }} />
                          <span style={{ fontWeight: '500', color: '#000' }}>New Agent</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
                
                <Form.Control.Feedback type="invalid">
                  {formErrors.agent}
                </Form.Control.Feedback>
              </div>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Agent Role</Form.Label>
              <Form.Select
                value={formData.agentRole}
                onChange={(e) => onInputChange("agentRole", e.target.value)}
                isInvalid={!!formErrors.agentRole}
              >
                <option value="">Select Agent Role</option>
                <option value="Buyer's Agent">Buyer's Agent</option>
                <option value="Seller's Agent">Seller's Agent</option>
                <option value="Others">Others</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formErrors.agentRole}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        
        {formData.agentRole === "Others" && (
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Specify Agent Role</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter agent role"
                  value={formData.specifyAgentRole}
                  onChange={(e) => onInputChange("specifyAgentRole", e.target.value)}
                  isInvalid={!!formErrors.specifyAgentRole}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.specifyAgentRole}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default AgentSection;